import React, { useState, createContext, useContext } from 'react';
interface SocialPost {
  id: string;
  userId: string;
  userName: string;
  image: string;
  description: string;
  likes: number;
  comments: number;
  createdAt: Date;
}
interface MysteryLunchMatch {
  id: string;
  partnerId: string;
  partnerName: string;
  date: Date;
  location: string;
  status: 'pending' | 'confirmed' | 'completed';
}
interface ThemedEvent {
  id: string;
  title: string;
  date: Date;
  theme: string;
  speaker: string;
  availableSeats: number;
  registered: boolean;
}
interface SocialContextType {
  posts: SocialPost[];
  mysteryLunchMatches: MysteryLunchMatch[];
  themedEvents: ThemedEvent[];
  addPost: (post: Omit<SocialPost, 'id' | 'createdAt'>) => void;
  likePost: (postId: string) => void;
  registerForMysteryLunch: () => Promise<void>;
  registerForThemedLunch: (eventId: string) => Promise<void>;
}
const SocialContext = createContext<SocialContextType | undefined>(undefined);
export const SocialProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [mysteryLunchMatches, setMysteryLunchMatches] = useState<MysteryLunchMatch[]>([]);
  const [themedEvents, setThemedEvents] = useState<ThemedEvent[]>([{
    id: 'event-1',
    title: 'Tech Thursday',
    date: new Date('2024-01-25'),
    theme: 'AI in Food Tech',
    speaker: 'Sarah Johnson',
    availableSeats: 20,
    registered: false
  }, {
    id: 'event-2',
    title: 'Finance Friday',
    date: new Date('2024-01-26'),
    theme: 'FinTech Innovations',
    speaker: 'Michael Chang',
    availableSeats: 15,
    registered: false
  }]);
  const addPost = (post: Omit<SocialPost, 'id' | 'createdAt'>) => {
    setPosts(prev => [{
      ...post,
      id: `post-${Date.now()}`,
      createdAt: new Date()
    }, ...prev]);
  };
  const likePost = (postId: string) => {
    setPosts(prev => prev.map(post => post.id === postId ? {
      ...post,
      likes: post.likes + 1
    } : post));
  };
  const registerForMysteryLunch = async () => {
    // Mock API call
    const match: MysteryLunchMatch = {
      id: `match-${Date.now()}`,
      partnerId: 'user-2',
      partnerName: 'Jane Smith',
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      location: 'Cafe Milano',
      status: 'pending'
    };
    setMysteryLunchMatches(prev => [match, ...prev]);
  };
  const registerForThemedLunch = async (eventId: string) => {
    setThemedEvents(prev => prev.map(event => event.id === eventId ? {
      ...event,
      registered: true,
      availableSeats: event.availableSeats - 1
    } : event));
  };
  return <SocialContext.Provider value={{
    posts,
    mysteryLunchMatches,
    themedEvents,
    addPost,
    likePost,
    registerForMysteryLunch,
    registerForThemedLunch
  }} data-id="element-196">
      {children}
    </SocialContext.Provider>;
};
export const useSocial = () => {
  const context = useContext(SocialContext);
  if (context === undefined) {
    throw new Error('useSocial must be used within a SocialProvider');
  }
  return context;
};